import {ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {FormControl, FormGroup} from '@angular/forms';
import {PageEvent} from '@angular/material/paginator';
import {Sort} from '@angular/material/sort';
import {ActivatedRoute} from '@angular/router';
import {CustomProtocol, FilterSort, Organization, OrganizationProtocolPreference} from '@core/data';
import {Dialog, Snackbar} from '@core/material';
import {Select, Store} from '@ngxs/store';
import {Observable, Subject} from 'rxjs';
import {debounceTime, filter, takeUntil} from 'rxjs/operators';
import {
    CustomProtocolFormDialog,
    CustomProtocolFormDialogData
} from '@app/evaluation/components/custom-protocol-index/custom-protocol-form.dialog';
import {
    CustomProtocolState,
    CustomProtocolIndexStateModel,
    FilterCustomProtocolIndex,
    PageCustomProtocolIndex,
    SortCustomProtocolIndex,
    ResetCustomProtocolIndex,
    CustomProtocolIndexQuery,
    InitCustomProtocolIndex, AddCustomProtocol, RemoveCustomProtocol,
    ReadCustomProtocol,
    AddProtocolPreference,
    LoadProtocolPreference,
    SetCustomProtocolIndexSelected,
    AddPreferenceWithoutProtocolId,
} from '@app/evaluation/components/custom-protocol-index/custom-protocol.state';
import { Crop, Library, Protocol, SampleType } from '@library';
import { LIBRARY } from '@app/evaluation/library';
import { SelectionModel } from '@angular/cdk/collections';
import { AuthState } from '@app/auth';

@Component({
    selector: 'pv-custom-protocol-index-view',
    templateUrl: './custom-protocol-index.view.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    preserveWhitespaces: false,
    host: {
        class: 'pv-cultivar-index-view',
    }
})
export class CustomProtocolIndexView implements OnInit, OnDestroy {

    @Select(CustomProtocolState)
    state$: Observable<CustomProtocolIndexStateModel>;

    @Select(CustomProtocolState.query)
    query$: Observable<CustomProtocolIndexQuery>;

    @Select(CustomProtocolState.sort)
    sort$: Observable<FilterSort>;

    @Select(CustomProtocolState.customProtocol)
    customProtocol$: Observable<CustomProtocol>;

    @Select(AuthState.selectedOrg)
    selectedOrg$: Observable<Organization>;

    // sort form group
    orderControl = new FormControl('desc');
    columnControl = new FormControl('createdAt');
    sortFormGroup = new FormGroup({
        column: this.columnControl,
        order: this.orderControl,
    });

    // filter controls
    searchControl = new FormControl(null);
    scionCropControl = new FormControl(null);
    sampleTypeControl = new FormControl([SampleType.PLANT, SampleType.HARVEST]);
    baseProtocolsControl = new FormControl(false);
    archivedControl = new FormControl(false);
    favouriteControl = new FormControl(true);
    hiddenControl = new FormControl(false);

    filterFormProtocol = new FormGroup({
        search: this.searchControl,
        crop: this.scionCropControl,
        sampleType: this.sampleTypeControl,
        baseProtocols: this.baseProtocolsControl,
        archived: this.archivedControl,
        favourite: this.favouriteControl,
        hidden: this.hiddenControl,
    });

    cropOptions: Crop[];
    sampleTypeOptions: {label: String, value: SampleType}[] = [
        {label: 'Plant', value: SampleType.PLANT},
        {label: 'Harvest', value: SampleType.HARVEST},
    ];

    selection = new SelectionModel<string>(true, []);

    isReadonly: boolean = false;

    private orgKey: string;
    private _destroy$ = new Subject();

    constructor(
        private _dialogs: Dialog,
        private _route: ActivatedRoute,
        private _snackbar: Snackbar,
        private _store: Store,
        @Inject(LIBRARY) private _library: Library,
    ) {
    }

    ngOnInit() {
        this._route.paramMap.subscribe(params => {
            this.orgKey = params.get('orgKey');
            this._store.dispatch(new InitCustomProtocolIndex(params.get('orgKey')));
        });

        // query state => filter form group
        this.query$
            .pipe(takeUntil(this._destroy$))
            .subscribe(query => {
                this.filterFormProtocol.patchValue({
                    search: query.search,
                    archived: query.archived ? query.archived : false,
                    baseProtocols: query.baseProtocols ? query.baseProtocols : false,
                    crop: query.crop ? query.crop : [],
                    sampleType: query.sampleType ? query.sampleType : [SampleType.PLANT, SampleType.HARVEST],
                    favourite: query.favourite ? query.favourite : false,
                    hidden: query.hidden ? query.hidden : false,
                }, {emitEvent: false});
            });

        // sort state => sort form group
        this.sort$
            .pipe(takeUntil(this._destroy$))
            .subscribe(sort => {
                this.sortFormGroup.setValue(sort, {emitEvent: false});
            });

        // filter form group => state changes
        this.filterFormProtocol.valueChanges
            .pipe(debounceTime(300), takeUntil(this._destroy$))
            .subscribe(value => {
                this._store.dispatch(new FilterCustomProtocolIndex(value));
            });

        // sort form group => state changes
        this.sortFormGroup.valueChanges
            .pipe(debounceTime(300), takeUntil(this._destroy$))
            .subscribe(value => {
                this._store.dispatch(new SortCustomProtocolIndex(value.column, value.order));
            });

        // open custom protocol from selector
        this.customProtocol$
            .pipe(takeUntil(this._destroy$), filter(result => !!result))
            .subscribe(result => {
                const data: CustomProtocolFormDialogData = {
                    data: result,
                }

                this.openProtocolDialog(result.ownerOrgKey, data);
            });

        this.selection.changed
            .pipe(takeUntil(this._destroy$), debounceTime(100))
            .subscribe(val => {
                this._store.dispatch(new SetCustomProtocolIndexSelected(this.selection.selected));
            });

        // Ensures that selection on UI always matches the state
        this.state$.subscribe(data => {
            if (data == null) {
                this.selection.clear();
                return;
            }

            if (data.selectedIds.length == 0) {
                this.selection.clear();
                return;
            }

            data.selectedIds.forEach(id => this.selection.select(id));
        });

        this.selectedOrg$.pipe(takeUntil(this._destroy$))
            .subscribe(organisation => {
                this.isReadonly = organisation?.access.role === 'r';
            });

        this.cropOptions = this._library.filterExcludedCrops()
    }

    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }

    reloadProtocols() {
        this._store.dispatch(new LoadProtocolPreference(this.orgKey));
    }

    addProtocol(orgKey: string, protocol?: Partial<Protocol>) {
        const data: CustomProtocolFormDialogData = {
            data: {
                title: 'New Protocol',
                ownerOrgKey: orgKey,
                protocol:true,
                filter:true,
                archived:false,
            },
        };

        if (!protocol) return this.openProtocolDialog(orgKey, data);

        this._store.dispatch(new ReadCustomProtocol(protocol.id));
    }

    openProtocolDialog(orgKey: string, data: CustomProtocolFormDialogData) {
        this._dialogs.open(CustomProtocolFormDialog, {data})
        .afterClosed()
        .subscribe(result => {
            if (!result) return;

            this._store.dispatch(new AddCustomProtocol(orgKey, result)).subscribe(_ => {
                this._snackbar.info(result.key ? 'Protocol updated' : 'Protocol created');
            });
        });
    }

    removeProtocol(report: CustomProtocol) {
        const data: CustomProtocolFormDialogData = {
            data: {...report},
        };

        this._dialogs.open(CustomProtocolFormDialog, {data})
            .afterClosed()
            .subscribe(result => {
                if (!result) return;
                this._store.dispatch(new RemoveCustomProtocol(result));
            });
    }

    toggleProtocolPreference(orgKey: string, protocol: Protocol, preference: string) {
        let status = protocol[preference] ? `un${preference}` : preference;
        if (preference == 'hidden') status = protocol[preference] ? `unhide` : 'hide';

        const updatedProtocol: Protocol = {
            ...protocol,
            [preference]: !protocol[preference]
        }

        this._dialogs.confirm(
            `${status[0].toUpperCase() + status.slice(1)} ${protocol.title}`,
            `Are you sure you want to ${status} this protocol?`,
            'Confirm',
            'Cancel',
            false,
        )
            .afterClosed()
            .subscribe(result => {
                if (!result) return;

                this._store.dispatch(new AddProtocolPreference(orgKey, updatedProtocol)).subscribe(res => {
                    this._snackbar.info(`Protocol preference updated`)
                });
            });
    }

    setBulkPreference(selectedIds: string[], preferences: string[], action: boolean) {
        if (!selectedIds.length) {
            this._snackbar.error('No Protocols Selected');
            return;
        }

        let status;
        const [ primary, secondary ] = preferences;
        const state: CustomProtocolIndexStateModel = this._store.selectSnapshot(CustomProtocolState);

        if (primary == 'hidden') status = action ? `hide` : 'unhide';
        else status = action ? `favourite` : 'unfavourite';

        this._dialogs.confirm(
            `${status[0].toUpperCase() + status.slice(1)} ${selectedIds.length} protocols`,
            `Are you sure you want to ${status} these protocols?`,
            'Confirm',
            'Cancel',
            false,
        )
            .afterClosed()
            .subscribe(result => {
                if (!result) return;

                let actions: AddPreferenceWithoutProtocolId[] = [];
                selectedIds.forEach(id => {
                    let preference: Partial<OrganizationProtocolPreference> = state.preferences.find(preference => {
                        return preference.protocolId === id && preference.ownerOrgKey === state.orgKey
                    });

                    if (!preference) {
                        preference = {
                            ownerOrgKey: state.orgKey,
                            protocolId: id,
                        }
                    }

                    //? SETS EITHER FAVOURITE/HIDE AND INVERTS THE OTHER ONE
                    preference = {
                        ...preference,
                        [primary]: action,
                        [secondary]: action == true ? !action : (preference[secondary] ?? false),
                    }

                    actions.push(new AddPreferenceWithoutProtocolId(preference));
                });

                this._store.dispatch(actions).subscribe(completed => {
                    if (completed) this._snackbar.info('Preferences updated');
                });
        });
    }

    reset() {
        this._store.dispatch(new ResetCustomProtocolIndex());
    }

    paginate(page: PageEvent) {
        this._store.dispatch(new PageCustomProtocolIndex(page.pageIndex, page.pageSize));
    }

    sort(sort: Sort) {
        if (sort.direction) {
            this.sortFormGroup.setValue({
                column: sort.active,
                order: sort.direction
            });
        } else {
            this.sortFormGroup.setValue({
                column: 'label',
                order: 'asc'
            });
        }
    }

    toggleSortOrder(event: MouseEvent) {
        event.stopPropagation();
        this.orderControl.setValue(this.orderControl.value === 'asc' ? 'desc' : 'asc');
    }

    isAllSelected(data: Protocol[]) {
        return data.every((protocol) => {
            return this.selection.isSelected(protocol.id);
        });
    }

    masterToggle(data: Protocol[]) {
        this.isAllSelected(data) ?
            data.forEach(row => this.selection.deselect(row.id)) :
            data.forEach(row => this.selection.select(row.id));
    }
}
